/**
 * useLinePointDrag Hook
 *
 * Provides drag functionality for individual line points.
 * Allows editing line shape by dragging specific points.
 *
 * Features:
 * - Drag individual points in linePoints array
 * - Real-time line update during drag
 * - Automatic center recalculation
 * - History integration via Zustand
 * - Security: Only works when conditions met (drawing mode, correct task)
 *
 * @module hooks/useLinePointDrag
 */

import { useCallback } from 'react';
import { useMap } from '@vis.gl/react-maplibre';
import maplibregl from 'maplibre-gl';
import type { MarkerDragEvent } from '@vis.gl/react-maplibre';
import { useDrawingTools } from './useDrawingTools';
import { useGeoMarkupStore } from '../store';
import { calculateLineCenter } from '../utils/geometry';
import { createLineGeoJson } from '../components/map/GeometryRenderer/LineRenderer/utils/lineGeoJsonHelpers';
import { useGeometryAutoPan } from './useGeometryAutoPan';

/**
 * Custom hook for individual line point drag functionality
 *
 * @param geometryId - The ID of the line geometry
 * @returns Object containing drag handlers
 */
export const useLinePointDrag = (geometryId: string) => {
  const { current: map } = useMap();
  const { updateGeometry, currentGeometries } = useDrawingTools();

  // Zustand drag state management
  const setDraggingGeometry = useGeoMarkupStore((s) => s.setDraggingGeometry);
  const setDraggingPointIndices = useGeoMarkupStore((s) => s.setDraggingPointIndices);
  const setWholeDragActive = useGeoMarkupStore((s) => s.setWholeDragActive);

  // Auto-pan support for smooth dragging experience
  const { smartPan, initializeVelocityTracking, stopAutoPan } = useGeometryAutoPan(geometryId);

  // Find the specific geometry we're working with
  const geometry = currentGeometries.find((g) => g.id === geometryId);

  /**
   * Handle point drag start event (SYNCHRONIZED MULTI-POINT)
   * HIDE NUMBER MARKERS - Hides number labels for ALL points at this coordinate
   * SET DRAGGING STATE - Automatically hides segment handles
   *
   * When dragging a point with duplicates (e.g., 1→2→3→1), ALL instances at
   * that coordinate will move together (synchronized drag).
   *
   * @param event - Drag event from MapLibre marker
   * @param pointIndices - Array of indices for all points at this coordinate
   *
   * @example
   * ```typescript
   * // Line 1→2→3→1: linePoints = [p1, p2, p3, p1_dup]
   * // User drags point 1: pointIndices = [0, 3]
   * // Both number markers (if visible) are hidden
   * ```
   */
  const handlePointDragStart = useCallback(
    (event: MarkerDragEvent, pointIndices: number[]) => {
      if (!geometry) return;

      // CRITICAL: Clear whole-drag flag FIRST - vertex drag takes priority over whole-line drag
      // Both hooks may fire for the same touch on mobile:
      // - Map's touchstart → useGeometryWholeDrag → sets isWholeDragActive = true
      // - Marker's onDragStart → useLinePointDrag → must clear it
      // The target.closest check in useGeometryWholeDrag fails because map's target is canvas.
      setWholeDragActive(false);

      // Set vertex drag state
      setDraggingPointIndices(pointIndices);
      setDraggingGeometry(geometryId);

      // Initialize velocity tracking for auto-pan
      initializeVelocityTracking(event.lngLat);
    },
    [geometry, geometryId, setWholeDragActive, setDraggingPointIndices, setDraggingGeometry, initializeVelocityTracking]
  );

  /**
   * Handle point drag event (REAL-TIME SYNCHRONIZED MULTI-POINT UPDATE)
   * Updates ALL points at this coordinate to show preview with new position
   * MapLibre Marker handles the visual point, we update the line preview
   *
   * Uses createLineGeoJson for proper interpolation (curved lines support)
   *
   * @param event - Drag event from MapLibre marker
   * @param pointIndices - Array of indices for all points at this coordinate
   *
   * @example
   * ```typescript
   * // Line 1→2→3→1: linePoints = [p1, p2, p3, p1_dup]
   * // User drags point 1: pointIndices = [0, 3]
   * // Updates BOTH linePoints[0] AND linePoints[3] to new coordinates
   * // Line stays closed: 1_new→2→3→1_new
   * ```
   */
  const handlePointDrag = useCallback(
    (event: MarkerDragEvent, pointIndices: number[]) => {
      if (!map || !geometry) return;

      const linePoints = geometry.linePoints || [];
      const { lat, lng } = event.lngLat;
      const newCoords = { lat, lng };

      // Auto-pan map when dragging near edges
      smartPan(event.lngLat);

      // Update ALL points at this coordinate (synchronized drag)
      const newLinePoints = [...linePoints];
      pointIndices.forEach((index) => {
        if (index >= 0 && index < linePoints.length) {
          newLinePoints[index] = newCoords;
        }
      });

      // REAL-TIME: Update line source to show preview with proper interpolation
      // ✅ DRY: Reuse createLineGeoJson (auto-applies interpolation for curved lines!)
      // This makes the line "rubber band" as you drag the point
      const source = map.getSource(`line-source-${geometryId}`) as maplibregl.GeoJSONSource;
      if (source && source.type === 'geojson') {
        const previewGeometry = { ...geometry, linePoints: newLinePoints };
        const geoJson = createLineGeoJson({ geometry: previewGeometry });
        source.setData(geoJson);
      }
    },
    [map, geometry, geometryId, smartPan]
  );

  /**
   * Handle point drag end event (SYNCHRONIZED MULTI-POINT FINALIZATION)
   * SINGLE ZUSTAND UPDATE - Updates ALL points at this coordinate atomically
   * SHOW NUMBER MARKERS - Restores opacity for ALL point number markers
   * CLEAR DRAGGING STATE - Re-shows segment handles automatically
   * This is the ONLY place where we persist geometry changes during point drag
   *
   * @param event - Drag event from MapLibre marker
   * @param pointIndices - Array of indices for all points at this coordinate
   *
   * @example
   * ```typescript
   * // Line 1→2→3→1: linePoints = [p1, p2, p3, p1_dup]
   * // User finishes dragging point 1: pointIndices = [0, 3]
   * // Updates BOTH linePoints[0] AND linePoints[3] in single Zustand action
   * // Triangle moves correctly, stays closed
   * ```
   */
  const handlePointDragEnd = useCallback(
    (event: MarkerDragEvent, pointIndices: number[]) => {
      if (!geometry) return;

      const linePoints = geometry.linePoints || [];
      const { lat, lng } = event.lngLat;
      const newCoords = { lat, lng };

      // Stop auto-panning
      stopAutoPan();

      // Update ALL points at this coordinate (synchronized finalization)
      const newLinePoints = [...linePoints];
      pointIndices.forEach((index) => {
        if (index >= 0 && index < linePoints.length) {
          newLinePoints[index] = newCoords;
        }
      });

      // Calculate center using shared utility (DRY principle)
      const newCenter = calculateLineCenter(newLinePoints) || geometry.calculatedCenter;

      // Single Zustand update with final position (triggers history)
      updateGeometry(geometryId, {
        linePoints: newLinePoints,
        calculatedCenter: newCenter,
      });

      // Clear dragging state (re-shows segment handles automatically)
      setDraggingGeometry(null);

      // Show ALL number markers again after drop via Zustand state
      // PointMarkers component will show markers when draggingPointIndices is null
      setDraggingPointIndices(null);
    },
    [geometry, geometryId, updateGeometry, setDraggingGeometry, setDraggingPointIndices, stopAutoPan]
  );

  return {
    /** Handler for point drag start event */
    handlePointDragStart,
    /** Handler for point drag event (real-time update) */
    handlePointDrag,
    /** Handler for point drag end event */
    handlePointDragEnd,
  };
};
